import { Link } from "@tanstack/react-router";
import type { SortingState } from "@tanstack/react-table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { clsx } from "clsx";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { Badge, Spinner } from "@/components/common";
import type { ManagedUser } from "@/types/rbac";
import { ExamDateBadge } from "./ExamDateBadge";
import { RoleBadge } from "./RoleBadge";
import { StatusBadge } from "./StatusBadge";
import { UserActionsDropdown } from "./UserActionsDropdown";
import { UserAvatar } from "./UserAvatar";

interface UsersTableProps {
  data: ManagedUser[];
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  onEdit: (user: ManagedUser) => void;
  onToggleStatus: (user: ManagedUser) => void;
  onDelete: (user: ManagedUser) => void;
  currentUserId: string;
  isLoading?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);

  if (diffMin < 1) {
    return "Właśnie";
  }
  if (diffMin < 60) {
    return `${diffMin} min temu`;
  }
  if (diffH < 24) {
    return `${diffH} godz. temu`;
  }
  if (diffD < 30) {
    return `${diffD} dn. temu`;
  }
  return date.toLocaleDateString("pl-PL");
}

const columnHelper = createColumnHelper<ManagedUser>();

export function UsersTable({
  data,
  sorting,
  onSortingChange,
  onEdit,
  onToggleStatus,
  onDelete,
  currentUserId,
  isLoading = false,
  canUpdate = true,
  canDelete = true,
}: UsersTableProps) {
  const columns = [
    columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
      id: "name",
      header: "Agent",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <UserAvatar user={row.original} />
          <div className="min-w-0">
            <Link
              to="/agents/$agentId"
              params={{ agentId: row.original.id }}
              className="block truncate text-sm font-medium text-main hover:text-adara-700 hover:underline"
              title={`${row.original.firstName} ${row.original.lastName}`}
            >
              {row.original.firstName} {row.original.lastName}
            </Link>
            <p className="truncate text-xs text-dim" title={row.original.email}>
              {row.original.email}
            </p>
          </div>
        </div>
      ),
    }),
    columnHelper.accessor("role.name", {
      id: "role",
      header: "Rola",
      cell: ({ row }) => <RoleBadge role={row.original.role} />,
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    }),
    columnHelper.display({
      id: "licensing",
      header: "Upoważnienia",
      cell: ({ row }) => {
        const count = row.original.authorizationCount ?? 0;
        const onboardingDone = row.original.onboardingDoneCount ?? 0;
        const onboardingTotal = row.original.onboardingTotalCount ?? 0;
        return (
          <Link
            to="/agents/$agentId"
            params={{ agentId: row.original.id }}
            className="flex flex-wrap items-center gap-1.5"
            aria-label={`Upoważnienia, egzamin i wdrożenie — ${row.original.firstName} ${row.original.lastName}`}
          >
            <Badge variant={count > 0 ? "brand" : "default"}>
              {count === 0 ? "brak" : `${count} TU`}
            </Badge>
            {(row.original.examCount ?? 0) > 0 && (
              <>
                <Badge variant="warning">
                  {row.original.examCount === 1
                    ? "egzamin"
                    : `egzaminy: ${row.original.examCount}`}
                </Badge>
                <ExamDateBadge examDate={row.original.examDate ?? null} />
              </>
            )}
            {onboardingDone < onboardingTotal && (
              <Badge variant="info" className="whitespace-nowrap">
                wdrożenie {onboardingDone}/{onboardingTotal}
              </Badge>
            )}
          </Link>
        );
      },
    }),
    columnHelper.accessor("lastLoginAt", {
      header: "Ostatnie logowanie",
      cell: ({ getValue }) => {
        const val = getValue();
        return (
          <span className="text-sm text-dim">
            {val ? formatRelativeTime(val) : "Nigdy"}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <UserActionsDropdown
          user={row.original}
          onEdit={onEdit}
          onToggleStatus={onToggleStatus}
          onDelete={onDelete}
          isSelf={row.original.id === currentUserId}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      ),
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      onSortingChange(next);
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
  });

  return (
    <div className="relative overflow-x-auto">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-panel/60">
          <Spinner />
        </div>
      )}

      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="border-b border-edge bg-subtle">
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                return (
                  <th
                    key={header.id}
                    className={clsx(
                      "px-4 py-3 text-left text-xs font-medium text-dim",
                      canSort && "cursor-pointer select-none",
                    )}
                    onClick={
                      canSort
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {canSort && (
                        <span className="text-faint">
                          {sorted === "asc" ? (
                            <ArrowUp size={14} />
                          ) : sorted === "desc" ? (
                            <ArrowDown size={14} />
                          ) : (
                            <ArrowUpDown size={14} />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="divide-y divide-edge-soft">
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="transition-colors hover:bg-subtle">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-4 py-3">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
